import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine, ErrorBar } from 'recharts';
import { DEMO_PATIENTS, DEMO_GONOGO, DEMO_VITAL_SIGNS, DEMO_CONSENT_RECORDS, DEMO_RECONSENT_RECORDS, DEMO_STOPPING_RULES, MTD_DECLARATION, SITE_MAP, VitalSign } from '../api/demoData';
import Glossary, { GlossaryEntry } from '../components/Glossary';
import ComplianceBadges from '../components/ComplianceBadges';
import ViewModeToggle from '../components/ViewModeToggle';
import { useViewMode } from '../components/ViewModeContext';
import { SafetyMonitorPanel } from '../components/MonitorOverlay';

const SAFETY_GLOSSARY: GlossaryEntry[] = [
  { term: 'CTCAE', definition: 'Common Terminology Criteria for Adverse Events \u2014 FDA standard grading 1\u20135' },
  { term: 'Grade 1', definition: 'Mild, no intervention needed' },
  { term: 'Grade 2', definition: 'Moderate, minimal intervention' },
  { term: 'Grade 3', definition: 'Severe, medical intervention required' },
  { term: 'Grade 4', definition: 'Life threatening, urgent intervention' },
  { term: 'Grade 5', definition: 'Death' },
  { term: 'DLT', definition: 'Dose Limiting Toxicity \u2014 AE that prevents dose escalation per protocol' },
  { term: 'Go/No-Go', definition: 'Committee recommendation on whether to escalate to next dose level' },
  { term: 'SDV', definition: 'Source Data Verification \u2014 confirming EDC data matches original source records' },
  { term: 'M1 Safety', definition: 'D3RI model scoring adverse event profile using CTCAE criteria' },
  { term: 'M6 SDV', definition: 'D3RI model verifying data against source documents' },
  { term: 'M8 Eligibility', definition: 'D3RI model confirming patient meets protocol inclusion criteria' },
  { term: 'M9 Go/No-Go', definition: 'D3RI synthesis model producing dose escalation recommendation' },
  { term: 'PHQ-9', definition: 'Patient Health Questionnaire \u2014 depression screening score 0\u201327' },
  { term: 'GAD-7', definition: 'Generalized Anxiety Disorder scale score 0\u201321' },
  { term: 'HRV', definition: 'Heart Rate Variability \u2014 measure of autonomic nervous system health' },
  { term: 'AQI', definition: 'Air Quality Index \u2014 environmental pollution level' },
  { term: 'CYP2D6', definition: 'Cytochrome P450 enzyme that metabolizes many drugs' },
  { term: 'HLA-B*57:01', definition: 'Genetic marker for drug hypersensitivity reactions' },
  { term: 'BRCA', definition: 'Tumor suppressor gene \u2014 germline mutation affects cancer risk and drug targets' },
];

const GRADE_COLORS: Record<number, string> = {
  1: '#48bb78', 2: '#ecc94b', 3: '#ed8936', 4: '#f56565', 5: '#c53030',
};

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  normal: { bg: 'rgba(72, 187, 120, 0.15)', text: '#48bb78', border: '#48bb78' },
  amber:  { bg: 'rgba(236, 201, 75, 0.15)', text: '#ecc94b', border: '#ecc94b' },
  red:    { bg: 'rgba(245, 101, 101, 0.15)', text: '#f56565', border: '#f56565' },
};

function VitalSignCell({ vital }: { vital: VitalSign }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const color = STATUS_COLORS[vital.status];
  const delayLabel = vital.recordingDelayMin < 60
    ? `${vital.recordingDelayMin}m`
    : `${(vital.recordingDelayMin / 60).toFixed(1)}h`;

  return (
    <td
      style={{ ...vitalStyles.td, position: 'relative' }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        backgroundColor: color.bg, border: `1px solid ${color.border}`,
        borderRadius: 6, padding: '4px 10px',
      }}>
        <span style={{ color: color.text, fontWeight: 600, fontSize: 14 }}>
          {vital.value}
        </span>
        <span style={{ color: '#718096', fontSize: 11 }}>{vital.unit}</span>
        <span style={{
          backgroundColor: '#2d3748', color: '#a0aec0', fontSize: 10,
          padding: '1px 6px', borderRadius: 8, marginLeft: 2,
        }}>
          {delayLabel} delay
        </span>
      </div>
      {showTooltip && (
        <div style={vitalStyles.tooltip}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#e2e8f0' }}>{vital.name}</div>
          <div style={{ fontSize: 12, color: '#cbd5e0', lineHeight: 1.5 }}>{vital.tooltip}</div>
        </div>
      )}
    </td>
  );
}

export default function SafetyDashboard() {
  const { viewMode } = useViewMode();
  return (
    <div style={styles.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <h2 style={styles.title}>Cohort Safety Dashboard</h2>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <ViewModeToggle />
          <Glossary entries={SAFETY_GLOSSARY} />
        </div>
      </div>
      <ComplianceBadges badges={["21 CFR 312", "ICH E6(R3)", "CTCAE v5.0", "MHRA Apr 2026"]} />
      {viewMode === 'monitor' && <SafetyMonitorPanel />}
      {viewMode === 'coordinator' && (
        <div style={{ background: '#1a202c', border: '1px solid #2d3748', borderRadius: 8, padding: '12px 16px', marginTop: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#63b3ed', marginBottom: 8 }}>To Do Today</div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' as const, fontSize: 12, color: '#cbd5e0' }}>
            <span>SYN-001: Vital signs, Lab panel due</span>
            <span>SYN-004: PK sample (V3), AE assessment due</span>
            <span>SYN-007: Physical exam, ECG due</span>
          </div>
        </div>
      )}

      {/* MTD Declaration Banner */}
      {MTD_DECLARATION.declared && (
        <div style={styles.mtdBanner}>
          <div style={styles.mtdTitle}>MTD Declared at {MTD_DECLARATION.mtdDose}</div>
          <div style={styles.mtdDesc}>
            {MTD_DECLARATION.reason}. Recommended Phase 2 Dose (RP2D): {MTD_DECLARATION.rp2dDose}
          </div>
          <div style={styles.mtdDate}>Declared {new Date(MTD_DECLARATION.declaredAt).toLocaleDateString()}</div>
        </div>
      )}

      {/* Stopping Rules */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Dose Escalation Stopping Rule</h3>
        <p style={{ color: '#a0aec0', fontSize: 12, marginBottom: 12, marginTop: -8 }}>
          Rule: 2 of 3 DLTs in any cohort triggers dose escalation stop
        </p>
        <div style={styles.cardRow}>
          {DEMO_STOPPING_RULES.map((rule) => {
            const statusColor = rule.status === 'green' ? '#48bb78' : rule.status === 'amber' ? '#ecc94b' : '#f56565';
            return (
              <div key={rule.cohort} style={{
                ...styles.card,
                borderLeft: `4px solid ${statusColor}`,
              }}>
                <div style={styles.cardLabel}>{rule.cohort} — {rule.site} ({rule.dose})</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 28, fontWeight: 700, color: statusColor }}>{rule.dltCount}</span>
                  <span style={{ color: '#718096', fontSize: 13 }}>of {rule.totalPatients} DLTs (threshold: {rule.threshold})</span>
                </div>
                <div style={{
                  display: 'inline-block', marginTop: 8, padding: '3px 12px', borderRadius: 12,
                  fontSize: 11, fontWeight: 700,
                  backgroundColor: rule.status === 'green' ? '#22543d' : rule.status === 'amber' ? '#744210' : '#742a2a',
                  color: statusColor,
                }}>{rule.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Go/No-Go per cohort */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Go/No-Go Confidence by Cohort</h3>
        <div style={styles.cardRow}>
          {DEMO_GONOGO.map((g) => (
            <div key={g.cohort} style={{
              ...styles.card,
              borderLeft: `4px solid ${g.recommendation === 'GO' ? '#48bb78' : g.recommendation === 'NO-GO' ? '#f56565' : '#ecc94b'}`,
            }}>
              <div style={styles.cardLabel}>{g.cohort}</div>
              <div style={styles.cardScore}>{(g.confidence * 100).toFixed(0)}%</div>
              <div style={{
                ...styles.cardBadge,
                backgroundColor: g.recommendation === 'GO' ? '#22543d' : g.recommendation === 'NO-GO' ? '#742a2a' : '#744210',
                color: g.recommendation === 'GO' ? '#48bb78' : g.recommendation === 'NO-GO' ? '#fc8181' : '#ecc94b',
              }}>
                {g.recommendation}
              </div>
              <div style={styles.cardDetail}>Safety: {(g.safetyScore * 100).toFixed(0)}% | SDV: {(g.sdvScore * 100).toFixed(0)}% | Completeness: {(g.completenessScore * 100).toFixed(0)}%</div>
            </div>
          ))}
        </div>
      </div>

      {/* Grade distribution chart */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>CTCAE Grade Distribution</h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={DEMO_PATIENTS} margin={{ top: 10, right: 30, bottom: 10, left: 0 }}>
            <XAxis dataKey="id" tick={{ fill: '#a0aec0', fontSize: 12 }} />
            <YAxis domain={[0, 5]} tick={{ fill: '#a0aec0' }} label={{ value: 'Grade', angle: -90, position: 'insideLeft', style: { fill: '#a0aec0' } }} />
            <Tooltip
              contentStyle={{ backgroundColor: '#1a202c', border: '1px solid #2d3748', color: '#e2e8f0' }}
              formatter={(value: any) => [`Grade ${value}`, 'CTCAE']}
            />
            <Bar dataKey="grade" radius={[4, 4, 0, 0]}>
              {DEMO_PATIENTS.map((p, i) => (
                <Cell key={i} fill={GRADE_COLORS[p.grade] || '#a0aec0'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Vital Signs panel */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Vital Signs Monitor</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Patient</th>
                <th style={styles.th}>Blood Pressure</th>
                <th style={styles.th}>Heart Rate</th>
                <th style={styles.th}>Temperature</th>
                <th style={styles.th}>Resp. Rate</th>
                <th style={styles.th}>SpO2</th>
              </tr>
            </thead>
            <tbody>
              {DEMO_VITAL_SIGNS.map((pv) => (
                <tr key={pv.patientId} style={
                  pv.vitals.some((v) => v.status === 'red')
                    ? { backgroundColor: 'rgba(245, 101, 101, 0.08)' }
                    : {}
                }>
                  <td style={vitalStyles.td}>
                    <div style={{ fontWeight: 600, color: '#e2e8f0' }}>{pv.patientId}</div>
                    <div style={{ fontSize: 11, color: '#718096' }}>{pv.cohort}</div>
                  </td>
                  {pv.vitals.map((v) => (
                    <VitalSignCell key={v.name} vital={v} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 16, fontSize: 11, color: '#718096' }}>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, backgroundColor: '#48bb78', marginRight: 4 }} />Normal</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, backgroundColor: '#ecc94b', marginRight: 4 }} />Warning</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, backgroundColor: '#f56565', marginRight: 4 }} />Critical</span>
          <span style={{ marginLeft: 8 }}>Hover over any reading for clinical context</span>
        </div>
      </div>

      {/* Patient table */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Patient Safety Profiles</h3>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Patient ID</th>
              <th style={styles.th}>Site</th>
              <th style={styles.th}>Cohort</th>
              <th style={styles.th}>Grade</th>
              <th style={styles.th}>DLT</th>
              <th style={styles.th}>AE Count</th>
              <th style={styles.th}>AE Profile</th>
              <th style={styles.th}>Consent Status</th>
              <th style={styles.th}>Re-consent (Amend. 1)</th>
            </tr>
          </thead>
          <tbody>
            {DEMO_PATIENTS.map((p) => {
              const consent = DEMO_CONSENT_RECORDS.find((c) => c.patientId === p.id);
              const reconsent = DEMO_RECONSENT_RECORDS.find((r) => r.patientId === p.id);
              return (
                <tr key={p.id} style={p.dltFlag ? { backgroundColor: 'rgba(245, 101, 101, 0.1)' } : {}}>
                  <td style={styles.td}>{p.id}</td>
                  <td style={styles.td}>
                    {(() => { const s = SITE_MAP[p.id]; return s ? <span style={{
                      padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                      backgroundColor: s.color + '22', color: s.color, border: `1px solid ${s.color}`,
                    }}>{s.label}</span> : null; })()}
                  </td>
                  <td style={styles.td}>{p.cohort}</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.gradeBadge, backgroundColor: GRADE_COLORS[p.grade] }}>
                      Grade {p.grade}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {p.dltFlag ? (
                      <span style={styles.dltBadge}>DLT</span>
                    ) : (
                      <span style={styles.noDlt}>--</span>
                    )}
                  </td>
                  <td style={styles.td}>{p.aeCount}</td>
                  <td style={{ ...styles.td, fontSize: 12, maxWidth: 300 }}>{p.aeProfile}</td>
                  <td style={styles.td}>
                    {consent?.consented ? (
                      <div>
                        <span style={styles.consentBadge}>Consented</span>
                        <div style={{ fontSize: 10, color: '#718096', marginTop: 2 }}>
                          {consent.consentDate} | Form {consent.formVersion}
                        </div>
                      </div>
                    ) : (
                      <span style={styles.missingBadge}>Missing</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    {reconsent?.status === 'reconsented' ? (
                      <div>
                        <span style={styles.consentBadge}>Re-consented</span>
                        <div style={{ fontSize: 10, color: '#718096', marginTop: 2 }}>{reconsent.reconsentDate}</div>
                      </div>
                    ) : reconsent?.status === 'pending' ? (
                      <span style={styles.pendingBadge}>Pending</span>
                    ) : (
                      <span style={styles.notRequiredBadge}>Not required</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* M22 Bayesian CRM Dose Escalation */}
      <CRMPanel />

    </div>
  );
}

// ---------------------------------------------------------------------------
// M22 Bayesian CRM Panel
// ---------------------------------------------------------------------------
interface CRMData {
  trial_id: string; current_dose_index: number;
  posterior_alpha: number[]; posterior_beta: number[];
  p_dlt_mean: number[]; p_dlt_lower: number[]; p_dlt_upper: number[];
  mtd_index: number; mtd_dose: number; mtd_p_dlt: number; mtd_credible_interval: number[];
  recommended_next_dose_index: number; recommended_next_dose: number;
  recommendation: string; recommendation_rationale: string; confidence: number;
  exceeds_target_toxicity: boolean[]; stopping_rule_triggered: boolean; susar_threshold_crossed: boolean;
}

const DOSE_LABELS = ['10mg', '25mg', '50mg', '100mg'];
const CRM_PAYLOAD = {
  trial_id: 'Sym004-09', dose_levels: [10, 25, 50, 100],
  dlt_counts: [0, 1, 0, 0], cohort_sizes: [3, 6, 0, 0],
  current_dose_index: 1, target_toxicity: 0.33,
};

const REC_COLORS: Record<string, { bg: string; text: string }> = {
  Escalate: { bg: '#22543d', text: '#48bb78' },
  Stay: { bg: '#744210', text: '#ecc94b' },
  'De-escalate': { bg: '#7b341e', text: '#ed8936' },
  Stop: { bg: '#742a2a', text: '#f56565' },
};

function CRMPanel() {
  const [crm, setCrm] = useState<CRMData | null>(null);

  useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_URL || '';
    fetch(`${API_BASE}/api/v1/models/dose-escalation/crm`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(CRM_PAYLOAD),
    })
      .then((r) => r.json())
      .then(setCrm)
      .catch(() => {
        // Fallback: compute locally
        const doseData = CRM_PAYLOAD.dose_levels.map((_, i) => {
          const k = CRM_PAYLOAD.dlt_counts[i], n = CRM_PAYLOAD.cohort_sizes[i];
          const a = 1 + k, b = 1 + (n - k);
          const mean = a / (a + b);
          return { a, b, mean, lower: mean * 0.4, upper: Math.min(mean * 1.8, 0.95) };
        });
        setCrm({
          trial_id: 'Sym004-09', current_dose_index: 1,
          posterior_alpha: doseData.map((d) => d.a), posterior_beta: doseData.map((d) => d.b),
          p_dlt_mean: doseData.map((d) => parseFloat(d.mean.toFixed(4))),
          p_dlt_lower: doseData.map((d) => parseFloat(d.lower.toFixed(4))),
          p_dlt_upper: doseData.map((d) => parseFloat(d.upper.toFixed(4))),
          mtd_index: 1, mtd_dose: 25, mtd_p_dlt: doseData[1].mean,
          mtd_credible_interval: [doseData[1].lower, doseData[1].upper],
          recommended_next_dose_index: 2, recommended_next_dose: 50,
          recommendation: 'Escalate', recommendation_rationale: 'P(DLT) at 25mg safely below target 0.33. Escalate to 50mg.',
          confidence: 0.82, exceeds_target_toxicity: doseData.map((d) => d.mean > 0.33),
          stopping_rule_triggered: false, susar_threshold_crossed: false,
        });
      });
  }, []);

  if (!crm) return null;

  const doseStatus = (i: number) => {
    if (CRM_PAYLOAD.cohort_sizes[i] === 0) return { label: 'Not tested', color: '#718096', bg: '#2d3748' };
    if (crm.p_dlt_mean[i] >= 0.33) return { label: 'Exceeds target', color: '#f56565', bg: '#742a2a' };
    if (crm.p_dlt_mean[i] >= 0.20) return { label: 'Near target', color: '#ecc94b', bg: '#744210' };
    return { label: 'Safe', color: '#48bb78', bg: '#22543d' };
  };

  const chartData = CRM_PAYLOAD.dose_levels.map((_, i) => ({
    dose: DOSE_LABELS[i],
    pDlt: crm.p_dlt_mean[i],
    errorLow: crm.p_dlt_mean[i] - crm.p_dlt_lower[i],
    errorHigh: crm.p_dlt_upper[i] - crm.p_dlt_mean[i],
  }));

  const rc = REC_COLORS[crm.recommendation] || REC_COLORS['Stay'];

  return (
    <div style={{ marginTop: 32 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0' }}>Dose Escalation — Bayesian CRM (M22)</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, color: '#63b3ed', backgroundColor: '#1a365d' }}>21 CFR 312.32</span>
          <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, color: '#63b3ed', backgroundColor: '#1a365d' }}>FDA Adaptive Design</span>
        </div>
      </div>

      {/* Protocol header */}
      <div style={{ backgroundColor: '#1a202c', border: '1px solid #2d3748', borderRadius: 8, padding: '12px 20px', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 24, fontSize: 13, color: '#a0aec0' }}>
          <span><strong style={{ color: '#e2e8f0' }}>Trial:</strong> Sym004-09 · Sym004 + FOLFIRI</span>
          <span><strong style={{ color: '#e2e8f0' }}>Design:</strong> 3+3 dose escalation</span>
          <span><strong style={{ color: '#e2e8f0' }}>Target toxicity:</strong> 33%</span>
          <span><strong style={{ color: '#e2e8f0' }}>Current cohort:</strong> Cohort 2 · 25mg</span>
        </div>
      </div>

      {/* Alert banners */}
      {crm.stopping_rule_triggered && (
        <div style={{ backgroundColor: '#742a2a', border: '1px solid #f56565', borderRadius: 8, padding: '12px 20px', marginBottom: 12 }}>
          <span style={{ color: '#f56565', fontSize: 14, fontWeight: 700 }}>{'\u26D4'} Stopping rule triggered — {'\u2265'}2 DLTs at current dose</span>
        </div>
      )}
      {crm.susar_threshold_crossed && (
        <div style={{ backgroundColor: '#742a2a', border: '1px solid #f56565', borderRadius: 8, padding: '12px 20px', marginBottom: 12 }}>
          <span style={{ color: '#f56565', fontSize: 14, fontWeight: 700 }}>{'\u26A0'} SUSAR threshold crossed — notify sponsor immediately</span>
        </div>
      )}

      {/* Dose probability table */}
      <div style={{ backgroundColor: '#1a202c', border: '1px solid #2d3748', borderRadius: 8, padding: '16px 20px', marginBottom: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Dose', 'Patients', 'DLTs', 'P(DLT)', '90% CI', 'Status'].map((h) => (
                <th key={h} style={{ ...styles.th, textAlign: h === 'Dose' ? 'left' : 'center' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {CRM_PAYLOAD.dose_levels.map((dose, i) => {
              const st = doseStatus(i);
              return (
                <tr key={dose} style={i === crm.current_dose_index ? { backgroundColor: 'rgba(66, 153, 225, 0.1)' } : {}}>
                  <td style={{ ...styles.td, fontWeight: 700 }}>{dose}mg {i === crm.current_dose_index && <span style={{ color: '#63b3ed', fontSize: 10 }}> (current)</span>}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{CRM_PAYLOAD.cohort_sizes[i]}</td>
                  <td style={{ ...styles.td, textAlign: 'center', color: CRM_PAYLOAD.dlt_counts[i] > 0 ? '#f56565' : '#718096', fontWeight: CRM_PAYLOAD.dlt_counts[i] > 0 ? 700 : 400 }}>{CRM_PAYLOAD.dlt_counts[i]}</td>
                  <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700, color: st.color }}>{crm.p_dlt_mean[i].toFixed(2)}</td>
                  <td style={{ ...styles.td, textAlign: 'center', fontSize: 12, color: '#a0aec0' }}>[{crm.p_dlt_lower[i].toFixed(2)}, {crm.p_dlt_upper[i].toFixed(2)}]</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, color: st.color, backgroundColor: st.bg }}>{st.label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* P(DLT) bar chart */}
      <div style={{ backgroundColor: '#1a202c', border: '1px solid #2d3748', borderRadius: 8, padding: '16px 20px', marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 12 }}>P(DLT) Posterior Distribution</div>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
            <XAxis dataKey="dose" tick={{ fill: '#a0aec0', fontSize: 12 }} />
            <YAxis domain={[0, 1]} tick={{ fill: '#a0aec0', fontSize: 11 }} tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`} />
            <Tooltip contentStyle={{ backgroundColor: '#1a202c', border: '1px solid #2d3748', borderRadius: 6 }} formatter={(v: any) => [`${(Number(v) * 100).toFixed(1)}%`, 'P(DLT)']} />
            <ReferenceLine y={0.33} stroke="#f56565" strokeDasharray="5 5" label={{ value: 'Target (33%)', fill: '#f56565', fontSize: 10, position: 'right' }} />
            <ReferenceLine y={0.50} stroke="#c53030" strokeDasharray="3 3" label={{ value: 'SUSAR (50%)', fill: '#c53030', fontSize: 10, position: 'right' }} />
            <Bar dataKey="pDlt" radius={[4, 4, 0, 0]}>
              <ErrorBar dataKey="errorHigh" direction="y" stroke="#a0aec0" strokeWidth={1.5} />
              {chartData.map((_, i) => {
                const st = doseStatus(i);
                return <Cell key={i} fill={st.color === '#718096' ? '#4a5568' : st.color} opacity={CRM_PAYLOAD.cohort_sizes[i] === 0 ? 0.3 : 0.8} />;
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Recommendation card */}
      <div style={{ backgroundColor: '#1a202c', border: '1px solid #2d3748', borderRadius: 8, padding: '20px 24px', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24 }}>
          <div>
            <span style={{
              display: 'inline-block', padding: '6px 20px', borderRadius: 20,
              fontSize: 16, fontWeight: 700, color: rc.text, backgroundColor: rc.bg, marginBottom: 12,
            }}>{crm.recommendation}</span>
            <div style={{ fontSize: 14, color: '#e2e8f0', marginBottom: 6 }}>
              Recommended next dose: <strong>{crm.recommended_next_dose}mg</strong>
            </div>
            <div style={{ fontSize: 13, color: '#a0aec0' }}>Confidence: {(crm.confidence * 100).toFixed(0)}%</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: '#e2e8f0', marginBottom: 8 }}>{crm.recommendation_rationale}</div>
            <div style={{ fontSize: 12, color: '#a0aec0' }}>
              Estimated MTD: <strong style={{ color: '#e2e8f0' }}>{crm.mtd_dose}mg</strong>
              <span style={{ color: '#718096' }}> (90% CI: [{(crm.mtd_credible_interval[0] * 100).toFixed(0)}%, {(crm.mtd_credible_interval[1] * 100).toFixed(0)}%] P(DLT))</span>
            </div>
          </div>
        </div>
      </div>

      {/* Decision Twin integration */}
      <div style={{ backgroundColor: '#171923', border: '1px solid #2d3748', borderRadius: 8, padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 12, color: '#718096' }}>
          M22 recommendation captured in Decision Twin <strong style={{ color: '#a0aec0' }}>dt-003</strong> · PI vote required to confirm escalation decision
        </div>
        <a href="/decisions" style={{ fontSize: 12, color: '#63b3ed', textDecoration: 'none' }}>View Decision Twin {'\u2192'}</a>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: 32 },
  title: { fontSize: 24, fontWeight: 700, color: '#e2e8f0', marginBottom: 24 },
  section: { marginBottom: 32 },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: '#cbd5e0', marginBottom: 16 },
  cardRow: { display: 'flex', gap: 20, flexWrap: 'wrap' },
  card: { backgroundColor: '#1a202c', borderRadius: 8, padding: 20, minWidth: 260, flex: 1 },
  cardLabel: { color: '#a0aec0', fontSize: 13, marginBottom: 8 },
  cardScore: { fontSize: 36, fontWeight: 700, color: '#e2e8f0' },
  cardBadge: { display: 'inline-block', padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 700, marginTop: 8 },
  cardDetail: { color: '#718096', fontSize: 11, marginTop: 12 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #2d3748', color: '#a0aec0', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 },
  td: { padding: '10px 12px', borderBottom: '1px solid #1a365d', color: '#e2e8f0', fontSize: 13 },
  gradeBadge: { padding: '2px 10px', borderRadius: 12, color: '#fff', fontSize: 11, fontWeight: 600 },
  dltBadge: { backgroundColor: '#f56565', color: '#fff', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 },
  noDlt: { color: '#4a5568' },
  consentBadge: { backgroundColor: '#22543d', color: '#48bb78', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 },
  missingBadge: { backgroundColor: '#742a2a', color: '#fc8181', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 },
  pendingBadge: { backgroundColor: '#744210', color: '#ecc94b', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 },
  notRequiredBadge: { backgroundColor: '#2d3748', color: '#718096', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 },
  mtdBanner: { backgroundColor: 'rgba(72, 187, 120, 0.12)', border: '1px solid #48bb78', borderRadius: 8, padding: '16px 24px', marginBottom: 24 },
  mtdTitle: { color: '#48bb78', fontSize: 18, fontWeight: 700 },
  mtdDesc: { color: '#cbd5e0', fontSize: 13, marginTop: 4 },
  mtdDate: { color: '#718096', fontSize: 11, marginTop: 6 },
};

const vitalStyles: Record<string, React.CSSProperties> = {
  td: { padding: '10px 8px', borderBottom: '1px solid #1a365d', color: '#e2e8f0', fontSize: 13, verticalAlign: 'middle' },
  tooltip: {
    position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
    backgroundColor: '#1a202c', border: '1px solid #4a5568', borderRadius: 8,
    padding: '12px 16px', width: 280, zIndex: 50, boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
    marginBottom: 8,
  },
};
